import React, { useEffect, useState, Suspense } from "react";
import { Button, Text, TextField, TextFieldVariant } from "@merge-api/merge-javascript-shared";
import {
  BlueprintCustomFunctionStep,
  BlueprintParameterValueConstant,
  BlueprintParameterValueReturnValue,
  BlueprintParameterValue,
  BlueprintParameterValueNestedParameterValues,
  BlueprintParameterValueType,
} from "../../../../models/Blueprints";
import useBlueprintContext from "../../context/useBlueprintContext";
import { BlueprintAvailableParameter } from "../../utils/BlueprintEditorUtils";
import max from "lodash/max";
import CustomFunctionTypeaheadFormField from "./CustomFunctionTypeaheadFormField";
import { Maximize } from "lucide-react";

const LazyCodeBlock = React.lazy(() => import("./CustomFunctionsModal"));

const BlueprintEditorRightPanelCustomFunctionForm = () => {
  const { selectedStep, updateStepParameterValue } = useBlueprintContext();
  const step = selectedStep as BlueprintCustomFunctionStep;
  const args = (step.parameter_values.arguments as BlueprintParameterValueNestedParameterValues)
    .nested_parameter_values;
  const code = (step.parameter_values.code as BlueprintParameterValueConstant).constant as string;
  const [unsavedCustomFunctionCode, setUnsavedCustomFunctionCode] = useState(code);
  const [isShowingCustomFunctionsModal, setIsShowingCustomFunctionsModal] = useState<boolean>(
    false
  );

  useEffect(() => {
    setUnsavedCustomFunctionCode(code);
  }, [selectedStep?.id]);

  const maxArgVal = max([...Object.keys(args).map((arg) => Number(arg.slice(1))), 0]) as number;

  const updateArgs = (newArgs: { [key: string]: BlueprintParameterValue | undefined }) =>
    updateStepParameterValue(step, "arguments", {
      nested_parameter_values: newArgs,
      value_type: BlueprintParameterValueType.nestedParameterValues,
    });

  const addArg = () =>
    updateArgs({
      ...args,
      ["x" + (maxArgVal + 1)]: {
        step_id: "",
        return_schema_path: [],
        request_return_value_path: [],
        value_type: BlueprintParameterValueType.returnValue,
      },
    });

  const updateExistingArg = (key: string, newValue: BlueprintParameterValueReturnValue | null) => {
    updateArgs({ ...args, [key]: newValue ?? undefined });
  };

  const deleteArg = (key: string) => {
    const { [key]: _, ...newArgs } = args;
    updateArgs(newArgs);
  };

  return (
    <>
      <Suspense fallback={<div />}>
        <LazyCodeBlock
          isShowingCustomFunctionsModal={isShowingCustomFunctionsModal}
          setIsShowingCustomFunctionsModal={setIsShowingCustomFunctionsModal}
        />
      </Suspense>

      <Text as="div" variant="pre-title" className="text-gray-40 my-2">
        Arguments
      </Text>
      {Object.entries(args).map(([key, value]) => (
        <div key={key}>
          <CustomFunctionTypeaheadFormField
            onChange={(options: BlueprintAvailableParameter[]) =>
              updateExistingArg(
                key,
                options[0]?.parameterValue as BlueprintParameterValueReturnValue | null
              )
            }
            currentParameterValue={value}
            onDelete={() => deleteArg(key)}
            title={key}
            subtitle={"Set the value for the argument."}
          />
        </div>
      ))}

      <Button className="mt-0 mb-3" fullWidth onClick={addArg}>
        Add new argument
      </Button>
      <hr />
      <Text as="div" variant="pre-title" className="text-gray-40 my-2">
        Code
      </Text>
      <Text variant="h6">{"def custom_function(" + Object.keys(args).join(",") + "): \n"}</Text>
      <Text className="text-gray-40 mb-1" variant="sm">
        Only Python will work for custom functions.
      </Text>
      <TextField
        multiline
        value={unsavedCustomFunctionCode}
        onChange={(e) => {
          setUnsavedCustomFunctionCode(e.target.value);
          updateStepParameterValue(step, "code", {
            constant: e.target.value,
            value_type: BlueprintParameterValueType.constant,
          });
        }}
        rows={3}
        variant={TextFieldVariant.BorderedCode}
      />

      <Button
        className="mt-9"
        fullWidth
        onClick={() => setIsShowingCustomFunctionsModal(true)}
        rightIcon={<Maximize size={16} />}
      >
        View custom functions
      </Button>
    </>
  );
};

export default BlueprintEditorRightPanelCustomFunctionForm;
